/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';

import { SearchContext } from 'components/views/search/context/SearchContext';
import { SET_AUTOCOMPLETE_PANEL_ACTIVE } from 'components/views/search/reducer/index';
import { KEY_VALUES } from 'helpers/constants';
import useIsMobile from 'hooks/useIsMobile';

import ButtonLink from 'components/designSystem/ButtonLink/ButtonLink';

import IconSearch from 'components/shared/SVG/IconSearch';

import styles from './RestaurantAndCuisineSearch.module.scss';

type OnAutocompleteChange = (a: EventTarget) => void;

interface RestaurantAndCuisineSearchProps {
  /** Calls the autocomplete API and updates autocomplete results depending on user input */
  onAutocompleteChange: OnAutocompleteChange;
  /** The submit event which calls the autocomplete API and closes the overlay */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

export default function RestaurantAndCuisineSearch({
  onAutocompleteChange,
  onSubmit,
}: RestaurantAndCuisineSearchProps) {
  const {
    state: {
      autocompletePanelActive,
      geocodedSearchedAddress,
      searchFiltersFromURL: { query },
    },
    dispatchToReducer,
  } = useContext(SearchContext);
  const isMobile = useIsMobile();
  const mobileSearchIsOpen = isMobile && autocompletePanelActive;
  const navInputRef = useRef<HTMLInputElement>();
  const [searchValue, setSearchValue] = useState(query);
  const showCloseButton = !isMobile && autocompletePanelActive;
  const placeholder = isMobile ? 'Find nearby restaurants' : 'Find nearby restaurants and cuisines';
  const router = useRouter();
  const cuisineParams = router?.query?.cuisines?.toString() || '';
  useEffect(() => {
    // Only update based on query param if 1) Using mobile search
    // 2) User lands on page with a query param
    if (document.activeElement !== navInputRef.current) setSearchValue(query);
  }, [query]);

  useEffect(() => {
    setSearchValue(cuisineParams);
  }, [cuisineParams]);

  useEffect(() => {
    if (!isMobile && autocompletePanelActive) {
      setTimeout(() => {
        navInputRef.current.focus();
      }, 10);
    }
  }, [autocompletePanelActive]);

  const debounceNavSearch = useCallback(
    debounce((e) => onAutocompleteChange(e), 500),
    [geocodedSearchedAddress]
  );

  function handleDebounce(e) {
    e.persist();
    setSearchValue(e.target.value);
    debounceNavSearch(e);
  }

  function handleEnterEscKey(e) {
    if (e.key === KEY_VALUES.enter || e.key === KEY_VALUES.escape) {
      navInputRef.current.blur();
      dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: false });
    }
  }

  return (
    <div
      className={styles.restaurantSearchWrapper}
      onKeyDown={handleEnterEscKey}
      tabIndex={-1}
      role="none"
    >
      <form
        className={styles.formWrapper}
        action=""
        autoComplete="false"
        spellCheck="false"
        onSubmit={onSubmit}
      >
        <input
          type="search"
          autoComplete="off"
          disabled={mobileSearchIsOpen}
          ref={navInputRef}
          onChange={handleDebounce}
          onClick={() => dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: true })}
          className={styles.restaurantSearchInput}
          value={searchValue}
          placeholder={placeholder}
        />
        <IconSearch className={styles.magnifyingGlass} />
      </form>
      {showCloseButton && (
        <ButtonLink
          onClick={() => dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: false })}
        >
          Close
        </ButtonLink>
      )}
    </div>
  );
}
